import React, { useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { useLang } from "@/i18n/LangContext";
import { FileDropzone } from "@/components/FileDropzone";
import { analyzeExcel, getExcelSheets, pollJob, apiErrorMessage } from "@/lib/dotecApi";
import { LoadingAd } from "@/components/ads/LoadingAd";
import { ProgressBar, stageLabel } from "@/components/ProgressBar";
import { InfoSections } from "@/components/InfoSections";
import { GUIDES } from "@/content/seoContent";
import { Loader2, BarChart3, Sparkles, MessageSquare, RotateCcw, ArrowLeft, Layers } from "lucide-react";

export default function ExcelAnalysis() {
  const { t, lang } = useLang();
  const [file, setFile] = useState(null);
  const [mode, setMode] = useState("general");
  const [question, setQuestion] = useState("");
  const [loading, setLoading] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [sheets, setSheets] = useState([]);
  const [selectedSheet, setSelectedSheet] = useState("");
  const [prog, setProg] = useState({ pct: 0, stage: "upload" });

  const onFileSelected = async (f) => {
    setFile(f);
    setAnalysis(null);
    setSheets([]);
    setSelectedSheet("");
    if (f) {
      try {
        setSheets(await getExcelSheets(f));
      } catch (_) {
        // sheet list is optional: analysis still works on all sheets
      }
    }
  };

  const handleAnalyze = async () => {
    if (!file) {
      toast.error(t.selectExcelFirst);
      return;
    }
    if (mode === "question" && !question.trim()) {
      toast.error(t.writeQuestion);
      return;
    }
    setLoading(true);
    setAnalysis(null);
    setProg({ pct: 5, stage: "upload" });
    try {
      const { job_id } = await analyzeExcel({ file, mode, instructions: question, sheet: selectedSheet });
      const job = await pollJob(job_id, {
        onProgress: (j) => setProg({ pct: j.progress ?? 0, stage: j.stage || "extract" }),
      });
      if (job.status === "error") {
        toast.error(job.error || t.genericError);
        return;
      }
      setAnalysis(job);
      toast.success(t.analysisDone);
    } catch (e) {
      toast.error(apiErrorMessage(e, t));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-4xl px-6 py-12 lg:px-8">
      <div className="mb-6 flex justify-start">
        <Link
          to="/"
          data-testid="back-home-link"
          className="group inline-flex items-center gap-2 rounded-full border-2 border-emerald-500 bg-emerald-50 px-5 py-2.5 text-sm font-bold text-emerald-700 shadow-sm transition-all hover:-translate-y-0.5 hover:bg-emerald-500 hover:text-white hover:shadow-md"
        >
          <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-1" />
          {t.backHome}
        </Link>
      </div>
      <div className="mb-8 text-center">
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-violet-100 text-violet-600">
          <BarChart3 className="h-7 w-7" />
        </div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
          {t.analysisTitle}
        </h1>
        <p className="mx-auto mt-3 max-w-2xl text-slate-500">{t.analysisSub}</p>
      </div>

      <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
        <FileDropzone
          file={file}
          onFile={onFileSelected}
          accept=".xlsx,.xls"
          title={t.dropExcelFile}
          hint={t.excelFileHint}
          onError={(m) => toast.error(m)}
        />

        {file && sheets.length > 1 && (
          <div className="mt-5 flex flex-wrap items-center gap-3 rounded-xl border border-violet-100 bg-violet-50/60 px-4 py-3">
            <span className="flex items-center gap-2 text-sm font-semibold text-violet-700">
              <Layers className="h-4 w-4" />
              {t.sheetSelectLabel}
            </span>
            <select
              data-testid="sheet-select"
              value={selectedSheet}
              onChange={(e) => setSelectedSheet(e.target.value)}
              className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 outline-none transition-colors focus:border-violet-400"
            >
              <option value="">{t.allSheets}</option>
              {sheets.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
        )}

        <div className="mt-6">
          <div className="mb-3 flex rounded-xl bg-slate-100 p-1">
            {[
              { key: "general", label: t.fullAnalysis, icon: Sparkles },
              { key: "question", label: t.askQuestion, icon: MessageSquare },
            ].map((m) => (
              <button
                key={m.key}
                data-testid={`analysis-mode-${m.key}`}
                onClick={() => setMode(m.key)}
                className={`flex flex-1 items-center justify-center gap-2 rounded-lg py-2.5 text-sm font-semibold transition-all ${
                  mode === m.key
                    ? "bg-white text-slate-900 shadow-sm"
                    : "text-slate-500 hover:text-slate-700"
                }`}
              >
                <m.icon className="h-4 w-4" />
                {m.label}
              </button>
            ))}
          </div>
          {mode === "question" && (
            <textarea
              data-testid="question-input"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder={t.questionPlaceholder}
              rows={3}
              className="w-full resize-none rounded-xl border border-slate-200 p-4 text-sm outline-none transition-colors focus:border-violet-400 focus:ring-2 focus:ring-violet-100"
            />
          )}
        </div>

        <button
          data-testid="analyze-button"
          onClick={handleAnalyze}
          disabled={loading || !file}
          className="mt-6 flex w-full items-center justify-center gap-2 rounded-xl bg-violet-600 py-4 text-base font-bold text-white shadow-sm transition-all hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {loading ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin" />
              {t.analyzing}
            </>
          ) : (
            <>
              <Sparkles className="h-5 w-5" />
              {t.analyzeBtn}
            </>
          )}
        </button>

        {loading && (
          <div className="mt-6">
            <ProgressBar progress={prog.pct} label={stageLabel(t, prog.stage)} />
          </div>
        )}
      </div>

      {/* Ad shown while the analysis is being processed */}
      {loading && <LoadingAd />}

      {analysis && (
        <div
          data-testid="analysis-result"
          className="dotec-fade-up mt-8 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8"
        >
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h2 className="font-display text-xl font-bold text-slate-900">
                {t.analysisResult}
              </h2>
              <p className="mt-1 text-sm text-slate-500">
                {analysis.filename} • {t.sheetsLabel}: {(analysis.sheets || []).join(", ") || "—"}
              </p>
            </div>
            <button
              onClick={() => setAnalysis(null)}
              className="flex items-center gap-2 rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-600 transition-colors hover:bg-slate-50"
            >
              <RotateCcw className="h-4 w-4" />
              {t.newBtn}
            </button>
          </div>
          <div className="whitespace-pre-wrap rounded-2xl bg-slate-50 p-6 text-sm leading-relaxed text-slate-700">
            {analysis.analysis}
          </div>
        </div>
      )}

      {/* Tool guide (SEO / AdSense long-form content) */}
      <div className="mt-4 rounded-3xl border border-slate-100 bg-white/60">
        <InfoSections sections={GUIDES.analysis[lang] || GUIDES.analysis.en} testId="analysis-guide" />
      </div>
    </div>
  );
}
